"use strict";

const PRESET_COMPARE_PARAM_KEYS = [
  "compare_items",
  "compare_item",
  "compare_ids",
  "compare_id",
  "compare_handles",
  "compare_handle",
];

const normalizeCompareItem = (value) => String(value ?? "").trim();

const normalizeCompareItems = (items) => {
  if (!Array.isArray(items)) return [];
  const unique = [];
  items.forEach((item) => {
    const value = normalizeCompareItem(item);
    if (!value || unique.includes(value)) return;
    unique.push(value);
  });
  return unique;
};

const getComparePageSection = () =>
  document.querySelector(".compare-page-section");

const getPresetCompareParamKeys = () => {
  const sectionEl = getComparePageSection();
  const rawKeys = sectionEl?.dataset.presetCompareParamKeys || "";
  const keys = rawKeys
    .split(",")
    .map((key) => normalizeCompareItem(key))
    .filter(Boolean);
  return keys.length ? keys : PRESET_COMPARE_PARAM_KEYS;
};

const createCompareSearchTerm = (rawValue, forcedField = "") => {
  const value = normalizeCompareItem(rawValue);
  if (!value) return "";

  if (/^(id|handle):/i.test(value)) {
    const [field, ...rest] = value.split(":");
    const normalizedField = field.toLowerCase();
    const normalizedValue = normalizeCompareItem(rest.join(":"));
    return normalizedValue ? `${normalizedField}:${normalizedValue}` : "";
  }

  if (forcedField === "id") {
    return /^\d+$/.test(value) ? `id:${value}` : "";
  }

  if (forcedField === "handle") {
    return `handle:${value}`;
  }

  return /^\d+$/.test(value) ? `id:${value}` : `handle:${value}`;
};

const createCompareSearchTermsFromIds = (items) =>
  normalizeCompareItems(items)
    .map((item) => createCompareSearchTerm(item, "id"))
    .filter(Boolean);

const buildCompareSearchQuery = (searchTerms) =>
  searchTerms
    .map((term) => normalizeCompareItem(term))
    .filter(Boolean)
    .join("%20OR%20");

const createPresetCompareState = () => {
  const params = new URLSearchParams(window.location.search);
  const rawItems = [];

  getPresetCompareParamKeys().forEach((key) => {
    params.getAll(key).forEach((value) => {
      value
        .split(",")
        .map((entry) => normalizeCompareItem(entry))
        .filter(Boolean)
        .forEach((entry) =>
          rawItems.push({
            key,
            value: entry,
          })
        );
    });
  });

  const searchTerms = [];
  const ids = [];

  rawItems.forEach(({ key, value }) => {
    const searchTerm = createCompareSearchTerm(
      value,
      /^compare_handles?$/i.test(key)
        ? "handle"
        : /^compare_ids?$/i.test(key)
          ? "id"
          : ""
    );
    if (!searchTerm || searchTerms.includes(searchTerm)) return;
    searchTerms.push(searchTerm);
    if (searchTerm.startsWith("id:")) {
      ids.push(searchTerm.slice(3));
    }
  });

  return {
    active: searchTerms.length > 0,
    items: normalizeCompareItems(ids),
    searchTerms,
    handleMap: {},
  };
};

const presetCompareState = createPresetCompareState();

const isPresetCompareMode = () => presetCompareState.active;

const createPresetCompareSearchTermsFromItems = (items) =>
  normalizeCompareItems(items)
    .map((item) => {
      const handle = normalizeCompareItem(presetCompareState.handleMap[item]);
      return handle ? `handle:${handle}` : createCompareSearchTerm(item, "id");
    })
    .filter(Boolean);

const getCompareSearchTerms = () => {
  if (isPresetCompareMode()) {
    if (presetCompareState.searchTerms.length) {
      return [...presetCompareState.searchTerms];
    }
    return createCompareSearchTermsFromIds(presetCompareState.items);
  }
  return createCompareSearchTermsFromIds(getCompareItems());
};

const fetchPresetCompareProductsByIds = async (productIds) => {
  const ids = normalizeCompareItems(productIds);
  const endpoint = normalizeCompareItem(window.STOREFRONT_GQL_ENDPOINT);
  const accessToken = normalizeCompareItem(window.STOREFRONT_ACCESS_TOKEN);
  if (!ids.length || !endpoint || !accessToken) {
    return [];
  }

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": accessToken,
    },
    body: JSON.stringify({
      query: `
        query CompareProductsByIds($ids: [ID!]!) {
          nodes(ids: $ids) {
            __typename
            ... on Product {
              id
              handle
            }
          }
        }
      `,
      variables: {
        ids: ids.map((id) => `gid://shopify/Product/${id}`),
      },
    }),
  });

  if (!response.ok) {
    throw new Error(`Compare preset lookup failed with status ${response.status}`);
  }

  const payload = await response.json();
  const nodes = Array.isArray(payload?.data?.nodes) ? payload.data.nodes : [];

  return nodes
    .map((node) => {
      const gid = normalizeCompareItem(node?.id);
      const handle = normalizeCompareItem(node?.handle);
      const numericId = gid.split("/").pop();
      if (!numericId || !handle) return null;
      return { id: numericId, handle };
    })
    .filter(Boolean);
};

const resolvePresetCompareSearchTerms = async () => {
  if (!isPresetCompareMode()) {
    return getCompareSearchTerms();
  }

  const handleTerms = [];
  const unresolvedIds = [];

  presetCompareState.searchTerms.forEach((term) => {
    if (/^handle:/i.test(term)) {
      if (!handleTerms.includes(term)) {
        handleTerms.push(term);
      }
      return;
    }
    if (/^id:/i.test(term)) {
      unresolvedIds.push(term.replace(/^id:/i, ""));
    }
  });

  if (!unresolvedIds.length) {
    return [...presetCompareState.searchTerms];
  }

  try {
    const resolvedProducts = await fetchPresetCompareProductsByIds(unresolvedIds);
    if (!resolvedProducts.length) {
      console.warn(
        "[compare] Preset compare IDs did not resolve to storefront products.",
        unresolvedIds
      );
    }
    resolvedProducts.forEach(({ id, handle }) => {
      presetCompareState.handleMap[id] = handle;
    });
  } catch (error) {
    console.error("[compare] Unable to resolve preset compare IDs.", error);
  }

  presetCompareState.searchTerms = [
    ...handleTerms,
    ...createPresetCompareSearchTermsFromItems(unresolvedIds),
  ].filter((term, index, items) => items.indexOf(term) === index);

  return [...presetCompareState.searchTerms];
};

const syncPresetCompareStateFromResults = (productCards) => {
  if (!isPresetCompareMode()) return;

  const resolvedIds = [];
  productCards.forEach((card) => {
    const productId = normalizeCompareItem(
      card.querySelector("compare-remove")?.dataset.productId
    );
    const productHandle = normalizeCompareItem(
      card.querySelector("compare-remove")?.dataset.productHandle
    );
    if (productId && productHandle) {
      presetCompareState.handleMap[productId] = productHandle;
    }
    if (!productId || resolvedIds.includes(productId)) return;
    resolvedIds.push(productId);
  });

  presetCompareState.items = resolvedIds;
  presetCompareState.searchTerms =
    createPresetCompareSearchTermsFromItems(resolvedIds);
};

const appendCompareMoreToRows = (compareDiv) => {
  const sectionEl = getComparePageSection();
  const allProductsUrl = sectionEl?.dataset.allProductsUrl || "/collections/all";
  const rows = document.querySelectorAll(
    ".compare-row-basic, .compare-table [data-compare-row]"
  );

  const appendCompareMore = (row, isBasicRow) => {
    if (!row) return;
    const existing = row.querySelector(".compare-add-more");
    if (existing) {
      existing.remove();
    }
    const cell = document.createElement("div");
    cell.className =
      "compare-value compare-add-more border-inline-end px-20 py-15";
    if (isBasicRow) {
      cell.innerHTML = `
        <a class="compare-add-more__inner no-underline rounded border flex flex-column align-center text-center" href="${allProductsUrl}">
          <span class="compare-add-more__label">Compare more products</span>
          <span class="compare-add-more__icon" aria-hidden="true">
            <svg width="42" height="42" viewBox="0 0 42 42" fill="none">
              <circle cx="21" cy="21" r="20" stroke="currentColor" stroke-width="2" />
              <path d="M21 13v16M13 21h16" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </span>
        </a>
      `;
    } else {
      cell.classList.add("flex", "align-center");
      cell.textContent = "-";
    }
    row.appendChild(cell);
  };

  rows.forEach((row) => {
    const isBasicRow = row.classList.contains("compare-row-basic");
    appendCompareMore(row, isBasicRow);
  });
};

const clearCompareValues = (compareDiv) => {
  const table = compareDiv?.querySelector(".compare-table");
  if (!table) return;
  const existItems = table.querySelectorAll(".compare-value");
  if (existItems.length !== 0) {
    existItems.forEach((ei) => {
      ei.remove();
    });
  }
};

const getCompareFilterTypes = (value) => {
  const types = [];
  String(value || "")
    .split(",")
    .map((type) => type.trim().toLowerCase())
    .filter(Boolean)
    .forEach((type) => {
      if (!types.includes(type)) {
        types.push(type);
      }
    });
  return types;
};

const updateCompareTabsCounts = (counts) => {
  const tabs = document.querySelectorAll(
    "[data-compare-tabs] .tab[data-filter-type]"
  );
  tabs.forEach((btn) => {
    const types = getCompareFilterTypes(btn.dataset.filterType);
    const count = types.reduce((sum, type) => sum + (counts[type] || 0), 0);
    const badge = btn.querySelector(".tab__badge");
    if (!badge) return;
    if (count > 0) {
      badge.textContent = count;
      badge.classList.remove("hidden");
      badge.setAttribute("aria-hidden", "false");
    } else {
      badge.textContent = "";
      badge.classList.add("hidden");
      badge.setAttribute("aria-hidden", "true");
    }
  });
};

const updateCompareTabsCountsFromTable = () => {
  const counts = {};
  const basicRow = document.querySelector(".compare-row-basic");
  if (!basicRow) {
    updateCompareTabsCounts(counts);
    return;
  }
  const cells = basicRow.querySelectorAll(".compare-value[data-product-type]");
  cells.forEach((cell) => {
    const type = (cell.dataset.productType || "").trim().toLowerCase();
    if (!type) return;
    counts[type] = (counts[type] || 0) + 1;
  });
  updateCompareTabsCounts(counts);
};

const dispatchCompareUpdated = () => {
  document.dispatchEvent(new CustomEvent("compare:updated"));
};

const getCompareItems = () => {
  if (isPresetCompareMode()) {
    return [...presetCompareState.items];
  }
  const compareStore = window.glozinCompareStore;
  if (compareStore?.getLocalItems) {
    return compareStore.getLocalItems();
  }
  try {
    const items = JSON.parse(localStorage.getItem("glozin__compare-items"));
    return normalizeCompareItems(items);
  } catch (error) {
    return [];
  }
};

const saveCompareItems = (items, options = {}) => {
  if (isPresetCompareMode()) {
    const nextItems = normalizeCompareItems(items);
    presetCompareState.items = nextItems;
    presetCompareState.searchTerms =
      createPresetCompareSearchTermsFromItems(nextItems);
    document.dispatchEvent(
      new CustomEvent("compare:storage-changed", {
        detail: { items: nextItems, source: "preset" },
      })
    );
    return nextItems;
  }
  const compareStore = window.glozinCompareStore;
  if (compareStore?.setAndSync) {
    return compareStore.setAndSync(items, options);
  }
  const nextItems = normalizeCompareItems(items);
  localStorage.setItem("glozin__compare-items", JSON.stringify(nextItems));
  return nextItems;
};

const measureCompareWidthExpression = (value, contextEl) => {
  const raw = (value || "").trim();
  if (!raw || !contextEl || typeof document === "undefined") return 0;
  if (typeof CSS !== "undefined" && !CSS.supports("width", raw)) return 0;

  const probe = document.createElement("div");
  const computedPosition = getComputedStyle(contextEl).position;
  const inlinePosition = contextEl.style.position;

  if (computedPosition === "static") {
    contextEl.style.position = "relative";
  }

  probe.style.position = "absolute";
  probe.style.left = "0";
  probe.style.top = "0";
  probe.style.width = raw;
  probe.style.height = "0";
  probe.style.padding = "0";
  probe.style.border = "0";
  probe.style.overflow = "hidden";
  probe.style.visibility = "hidden";
  probe.style.pointerEvents = "none";

  contextEl.appendChild(probe);
  const measuredWidth = probe.getBoundingClientRect().width;
  probe.remove();

  if (computedPosition === "static") {
    if (inlinePosition) {
      contextEl.style.position = inlinePosition;
    } else {
      contextEl.style.removeProperty("position");
    }
  }

  return Number.isFinite(measuredWidth) ? measuredWidth : 0;
};

const resolveCompareWidth = (value, base, contextEl) => {
  const raw = (value || "").trim();
  if (!raw) return 0;
  if (raw.endsWith("px")) return parseFloat(raw);
  if (raw.endsWith("%")) return (parseFloat(raw) / 100) * base;
  if (raw.endsWith("rem")) {
    const rootSize = parseFloat(
      getComputedStyle(document.documentElement).fontSize
    );
    return parseFloat(raw) * (rootSize || 16);
  }
  if (raw.endsWith("em")) {
    const contextSize = parseFloat(getComputedStyle(contextEl).fontSize);
    return parseFloat(raw) * (contextSize || 16);
  }
  const numeric = parseFloat(raw);
  if (Number.isFinite(numeric)) return numeric;
  return measureCompareWidthExpression(raw, contextEl);
};

const applyCompareFixedColumnWidths = () => {
  const table = document.querySelector(".compare-table");
  if (!table || table.classList.contains("hidden")) return;

  // Read the active CSS breakpoint widths, then lock them in pixels so sticky rows stay aligned.
  table.style.removeProperty("--compare-field-width");
  table.style.removeProperty("--compare-col-width");

  const baseWidth = table.getBoundingClientRect().width;
  const computed = getComputedStyle(table);
  const cssFieldWidth = Math.round(
    resolveCompareWidth(
      computed.getPropertyValue("--compare-field-width"),
      baseWidth,
      table
    )
  );
  const cssColWidth = Math.round(
    resolveCompareWidth(
      computed.getPropertyValue("--compare-col-width"),
      baseWidth,
      table
    )
  );

  const fieldCell = table.querySelector(".compare-field");
  const valueCell = table.querySelector(".compare-value");
  if (!fieldCell || !valueCell) {
    table.classList.remove("is-fixed");
    return;
  }

  const fieldWidth =
    cssFieldWidth || Math.round(fieldCell.getBoundingClientRect().width);
  const valueWidth =
    cssColWidth || Math.round(valueCell.getBoundingClientRect().width);
  if (!fieldWidth || !valueWidth) {
    table.classList.remove("is-fixed");
    return;
  }

  table.style.setProperty("--compare-field-width", `${fieldWidth}px`);
  table.style.setProperty("--compare-col-width", `${valueWidth}px`);
  table.classList.add("is-fixed");
};

let compareWidthTimer;
const scheduleCompareWidthUpdate = (delay = 0) => {
  window.clearTimeout(compareWidthTimer);
  compareWidthTimer = window.setTimeout(() => {
    window.requestAnimationFrame(applyCompareFixedColumnWidths);
  }, delay);
};

document.addEventListener("compare:updated", () => {
  scheduleCompareWidthUpdate();
});
window.addEventListener("resize", () => {
  scheduleCompareWidthUpdate(150);
});

var BlsComparePageShopify = (function () {
  return {
    initCompareItems: function () {
      this.init();
    },

    init: async function () {
      const compareSearchTerms = isPresetCompareMode()
        ? await resolvePresetCompareSearchTerms()
        : getCompareSearchTerms();
      const compareDiv = document.querySelector(".compare-page-main");
      const div_no_product = compareDiv.querySelector(".compare-no-product-js");
      const table = compareDiv.querySelector(".compare-table");
      if (compareSearchTerms.length === 0) {
        if (table) {
          table.classList.remove("hidden");
        }
        div_no_product.classList.add("hidden");
        clearCompareValues(compareDiv);
        appendCompareMoreToRows(compareDiv);
        updateCompareTabsCounts({});
        dispatchCompareUpdated();
        return;
      } else {
        var query = buildCompareSearchQuery(compareSearchTerms);
        const section_id = document.querySelector(".compare-page-section")
          .dataset.sectionId;
        var productAjaxURL =
          "?section_id=" +
          section_id +
          "&type=product&options[unavailable_products]=last&q=" +
          query;
        fetch(`${window.routes.search_url}${productAjaxURL}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            const table = compareDiv.querySelector(".compare-table");
            const resultsMeta = html.querySelector(".compare-results-count");
            const debugEl = document.querySelector("[data-compare-debug]");
            if (resultsMeta && debugEl) {
              const count = Number(resultsMeta.dataset.resultsCount || 0);
              debugEl.textContent =
                count > 0 ? `Results1: ${count}` : `Results2: ${count}`;
            }

            const bpc = html.querySelectorAll(
              ".compare-page-section > .product-compare"
            );
            if (bpc.length > 0) {
              syncPresetCompareStateFromResults([...bpc]);
              if (table) {
                table.classList.remove("hidden");
                clearCompareValues(compareDiv);
              }
              const tableRows = table?.querySelectorAll(
                ".compare-row-basic, [data-compare-row]"
              );
              const rowMap = {};
              if (tableRows) {
                tableRows.forEach((row) => {
                  if (row.classList.contains("compare-row-basic")) {
                    rowMap.basic = row;
                  } else if (row.dataset.compareRow) {
                    rowMap[row.dataset.compareRow] = row;
                  }
                });
              }
              const counts = {};
              bpc.forEach((el) => {
                const type = (el.dataset.productType || "").trim().toLowerCase();
                if (type) {
                  counts[type] = (counts[type] || 0) + 1;
                }
                if (rowMap.basic) {
                  const sourceBasic = el.querySelector(".compare-row-basic");
                  if (sourceBasic) {
                    rowMap.basic.insertAdjacentHTML(
                      "beforeend",
                      sourceBasic.innerHTML
                    );
                  }
                }
                Object.keys(rowMap).forEach((key) => {
                  if (key === "basic") return;
                  const sourceRow = el.querySelector(
                    `[data-compare-row="${key}"]`
                  );
                  if (sourceRow && rowMap[key]) {
                    rowMap[key].insertAdjacentHTML(
                      "beforeend",
                      sourceRow.innerHTML
                    );
                  }
                });
              });
              appendCompareMoreToRows(compareDiv);
              updateCompareTabsCounts(counts);
              dispatchCompareUpdated();
            } else {
              syncPresetCompareStateFromResults([]);
              if (table) {
                table.classList.remove("hidden");
              }
              div_no_product.classList.add("hidden");
              clearCompareValues(compareDiv);
              appendCompareMoreToRows(compareDiv);
              updateCompareTabsCounts({});
              dispatchCompareUpdated();
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally((e) => {});
      }
    },
  };
})();
BlsComparePageShopify.initCompareItems();

class CompareRemove extends HTMLElement {
  constructor() {
    super();
    this.productId = this.dataset.productId;
    this.productHandle = this.dataset.productHandle;
    this.init();
  }
  init() {
    this.addEventListener("click", this.onThisClick.bind(this));
  }
  onThisClick() {
    const localListProductIds = getCompareItems();
    const allThisProductIds = document.querySelectorAll(
      `compare-remove[data-product-id="${this.productId}"]`
    );
    let listProductIds = [];
    if (!this.productId) return;
    if (localListProductIds.length > 0 && allThisProductIds.length > 0) {
      // compare localstorage exists
      listProductIds = [...localListProductIds].filter(
        (productId) => productId !== this.productId
      );
      const product_remove = document.querySelectorAll(
        `[data-product-handle="${this.productHandle}"]`
      );
      product_remove.forEach((e) => {
        e.remove();
      });
      if (listProductIds.length === 0) {
        const compareDiv = document.querySelector(".compare-page-main");
        const div_no_product = compareDiv.querySelector(
          ".compare-no-product-js"
        );
        const compare_table = compareDiv.querySelector(".compare-table");
        if (compare_table) {
          compare_table.classList.remove("hidden");
        }
        div_no_product.classList.add("hidden");
        clearCompareValues(compareDiv);
        appendCompareMoreToRows(compareDiv);
      }
      updateCompareTabsCountsFromTable();
      dispatchCompareUpdated();
    }
    saveCompareItems(listProductIds);
  }
}
customElements.define("compare-remove", CompareRemove);

const initCompareMoreDialog = () => {
  if (window.compareMoreDialogInitialized) return;
  window.compareMoreDialogInitialized = true;

  const dialog = document.querySelector("[data-compare-more-dialog]");
  if (!dialog) return;

  const animationContainer = dialog.querySelector(
    "[data-compare-more-animation]"
  );
  const timerElement = dialog.querySelector("[data-compare-more-timer]");
  const lottieSrc = dialog.dataset.lottieSrc;
  const loopCount = Math.max(1, Number(dialog.dataset.loopCount || 3));
  const canShowModal = typeof dialog.showModal === "function";
  let lottieInstance = null;
  let lottieLoaderPromise = null;
  let lottieReadyPromise = null;
  let lottieIsReady = false;
  let lottieDuration = 0;
  let activeCompleteHandler = null;
  let currentToken = 0;
  let safetyTimer = null;
  let countdownTimer = null;
  let countdownEndTime = 0;

  const clearSafetyTimer = () => {
    if (safetyTimer) {
      window.clearTimeout(safetyTimer);
      safetyTimer = null;
    }
  };

  const getClosingText = (seconds) =>
    `Redirecting in ${seconds}s`;

  const updateCountdownTimer = () => {
    if (!timerElement || !countdownEndTime) return;

    const secondsRemaining = Math.max(
      0,
      Math.ceil((countdownEndTime - Date.now()) / 1000)
    );

    timerElement.textContent = getClosingText(secondsRemaining);

    if (secondsRemaining <= 0 && countdownTimer) {
      window.clearInterval(countdownTimer);
      countdownTimer = null;
    }
  };

  const clearCountdownTimer = () => {
    if (countdownTimer) {
      window.clearInterval(countdownTimer);
      countdownTimer = null;
    }
    countdownEndTime = 0;
    if (timerElement) {
      timerElement.hidden = true;
    }
  };

  const startCountdownTimer = (durationMs) => {
    if (!timerElement || !Number.isFinite(durationMs) || durationMs <= 0) {
      return;
    }

    clearCountdownTimer();
    countdownEndTime = Date.now() + durationMs;
    timerElement.hidden = false;
    updateCountdownTimer();
    countdownTimer = window.setInterval(updateCountdownTimer, 250);
  };

  const loadLottie = () => {
    if (window.lottie) return Promise.resolve(window.lottie);
    if (lottieLoaderPromise) return lottieLoaderPromise;

    lottieLoaderPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie.min.js";
      script.async = true;
      script.onload = () => {
        if (window.lottie) {
          resolve(window.lottie);
        } else {
          reject(new Error("Lottie failed to initialize."));
        }
      };
      script.onerror = () =>
        reject(new Error("Failed to load Lottie script."));
      document.head.appendChild(script);
    });

    return lottieLoaderPromise;
  };

  const openDialog = () => {
    if (canShowModal) {
      if (!dialog.open) dialog.showModal();
      return;
    }
    dialog.setAttribute("open", "");
  };

  const closeDialog = () => {
    if (canShowModal) {
      if (dialog.open) dialog.close();
      return;
    }
    dialog.removeAttribute("open");
  };

  const removeCompleteHandler = () => {
    if (
      lottieInstance &&
      activeCompleteHandler &&
      typeof lottieInstance.removeEventListener === "function"
    ) {
      lottieInstance.removeEventListener("complete", activeCompleteHandler);
    }
    activeCompleteHandler = null;
  };

  const resetPreparedAnimation = () => {
    if (!lottieInstance) return;
    if (typeof lottieInstance.stop === "function") {
      lottieInstance.stop();
    }
    if (typeof lottieInstance.goToAndStop === "function") {
      lottieInstance.goToAndStop(0, true);
    }
  };

  const cleanupAnimation = () => {
    clearSafetyTimer();
    clearCountdownTimer();
    removeCompleteHandler();
    resetPreparedAnimation();
  };

  const prepareAnimation = () => {
    if (!lottieSrc || !animationContainer) {
      return Promise.reject(new Error("Missing Lottie animation source."));
    }
    if (lottieInstance && lottieIsReady) {
      return Promise.resolve(lottieInstance);
    }
    if (lottieReadyPromise) return lottieReadyPromise;

    lottieReadyPromise = loadLottie().then(
      (lottie) =>
        new Promise((resolve, reject) => {
          let settled = false;

          const handleReady = () => {
            if (settled) return;
            settled = true;
            lottieIsReady = true;
            lottieDuration = lottieInstance.getDuration(false);
            resetPreparedAnimation();
            resolve(lottieInstance);
          };

          const handleFailure = () => {
            if (settled) return;
            settled = true;
            lottieReadyPromise = null;
            lottieIsReady = false;
            if (
              lottieInstance &&
              typeof lottieInstance.destroy === "function"
            ) {
              lottieInstance.destroy();
            }
            lottieInstance = null;
            if (animationContainer) {
              animationContainer.innerHTML = "";
            }
            reject(new Error("Failed to load Lottie animation data."));
          };

          lottieInstance = lottie.loadAnimation({
            container: animationContainer,
            renderer: "svg",
            loop: false,
            autoplay: false,
            path: lottieSrc,
          });

          lottieInstance.addEventListener("DOMLoaded", handleReady);
          lottieInstance.addEventListener("data_failed", handleFailure);
        })
    );

    return lottieReadyPromise;
  };

  const playAnimation = async (redirectUrl) => {
    if (!redirectUrl) return;
    if (!lottieSrc || !animationContainer) {
      window.location.href = redirectUrl;
      return;
    }

    const token = ++currentToken;
    let playedLoops = 0;

    openDialog();
    cleanupAnimation();

    const finish = () => {
      if (token !== currentToken) return;
      cleanupAnimation();
      closeDialog();
      window.location.href = redirectUrl;
    };

    try {
      const animation = await prepareAnimation();
      if (token !== currentToken) return;

      activeCompleteHandler = () => {
        playedLoops += 1;
        if (playedLoops < loopCount) {
          animation.goToAndPlay(0, true);
        } else {
          finish();
        }
      };
      animation.addEventListener("complete", activeCompleteHandler);

      const duration = lottieDuration || animation.getDuration(false);
      if (Number.isFinite(duration) && duration > 0) {
        const durationMs = Math.ceil(duration * 1000 * loopCount);
        startCountdownTimer(durationMs);
        clearSafetyTimer();
        safetyTimer = window.setTimeout(finish, durationMs + 500);
      }

      animation.goToAndPlay(0, true);
    } catch (error) {
      console.error("[compare] Unable to load Lottie animation.", error);
      finish();
    }
  };

  document.addEventListener("click", (event) => {
    const link = event.target.closest(".compare-add-more__inner");
    if (!link) return;
    if (event.defaultPrevented) return;
    if (event.button && event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const href = link.getAttribute("href");
    if (!href) return;
    event.preventDefault();
    playAnimation(href);
  });

  dialog.addEventListener("cancel", (event) => {
    event.preventDefault();
  });

  dialog.addEventListener("close", () => {
    cleanupAnimation();
  });

  window.setTimeout(() => {
    prepareAnimation().catch((error) => {
      console.error("[compare] Unable to preload Lottie animation.", error);
    });
  }, 0);
};

initCompareMoreDialog();
